import { useState, useMemo, useCallback, useEffect } from 'react'
import { ImportedGood } from '@/features/cbam-reporting/hooks/importedGoods'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { Importer } from '@/features/importers/hooks/importers'
import { getImportedGoodEligibility } from '@/shared/utils/importedGoodEligibility'
import {
  productionMethodsByCode,
  ProductionMethods,
  AggregatedGoodsCategory
} from '@/common/config/productionMethods'
import { QUARTERS } from '@/config/cbamReportingPeriods'
import { trackCbamReportingSignal } from '@/features/cbam-reporting/analytics/cbamReportingClarity'

// Special value for "None" filter options
export const FILTER_NONE_VALUE = '__NONE__'

export type FilterType =
  | 'aggregatedGoodsCategory'
  | 'cnCode'
  | 'supplier'
  | 'installation'
  | 'emissionsReport'
  | 'country'
  | 'importer'
  | 'seller'
  | 'grrFilename'
  | 'quarter'

/** Filter types that allow at most one active value per type (new selection replaces the previous one). */
export const SINGLE_SELECT_FILTER_TYPES: ReadonlySet<FilterType> = new Set(['grrFilename'])

export type FilterValue = {
  type: FilterType
  value: string
  label: string
}

export type ToggleFilters = {
  showOnlyDefaultValues: boolean
  showOnlyIncomplete: boolean
}

export type FilterOptions = {
  aggregatedGoodsCategories: { value: string; label: string }[]
  cnCodes: { value: string; label: string }[]
  suppliers: { value: string; label: string }[]
  installations: { value: string; label: string }[]
  emissionsReports: { value: string; label: string }[]
  countries: { value: string; label: string }[]
  importers: { value: string; label: string }[]
  sellers: { value: string; label: string }[]
  grrFilenames: { value: string; label: string }[]
  quarters: { value: string; label: string }[]
}

export type ImportedGoodExtended = ImportedGood & {
  emissionsReport: SupplierEmissionsReport | null
  goodsReceivedReportFileName: string | null
}

// Default label formatters
const defaultCountryLabel = (code: string) => code.toUpperCase()

const defaultCategoryLabel = (category: AggregatedGoodsCategory) =>
  category
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, char => char.toUpperCase())

const defaultCnCodeLabel = (code: string) => code

// Individual filter functions
const isNoneFilter = (value: string): boolean => value === FILTER_NONE_VALUE

const filterByAggregatedGoodsCategory = (good: ImportedGood, value: string): boolean => {
  const method = productionMethodsByCode[good.productionMethodCode as ProductionMethods]
  return method?.aggregatedGoodsCategory === value
}

const filterByCnCode = (good: ImportedGood, value: string): boolean => good.cnCode === value

const filterBySupplier = (good: ImportedGood, value: string): boolean =>
  isNoneFilter(value) ? !good.partnershipId : good.partnershipId === value

const filterByInstallation = (good: ImportedGood, value: string): boolean =>
  isNoneFilter(value) ? !good.installationId : good.installationId === value

const filterByEmissionsReport = (good: ImportedGood, value: string): boolean =>
  isNoneFilter(value) ? !good.supplierEmissionsReportId : good.supplierEmissionsReportId === value

const filterByCountry = (good: ImportedGood, value: string): boolean => good.country === value

const filterByImporter = (good: ImportedGood, value: string): boolean =>
  isNoneFilter(value) ? !good.importerId : good.importerId === value

const filterBySeller = (good: ImportedGood, value: string): boolean =>
  isNoneFilter(value) ? !good.sellerName : good.sellerName?.toLowerCase() === value.toLowerCase()

const filterByGrrFilename = (good: ImportedGoodExtended, value: string): boolean => {
  const fileName = good.goodsReceivedReportFileName
  return isNoneFilter(value) ? !fileName : fileName === value
}

const filterByQuarter = (good: ImportedGood, value: string): boolean => good.quarter === value

// Toggle filter functions
const filterByDefaultValues = (good: ImportedGood): boolean => good.isUsingDefaultEmissions

const filterByIncomplete = (good: ImportedGood, isRepresentative: boolean): boolean =>
  getImportedGoodEligibility(good, isRepresentative).status === 'INCOMPLETE'

const filterFunctions: Record<
  FilterType,
  (good: ImportedGoodExtended, value: string) => boolean
> = {
  aggregatedGoodsCategory: filterByAggregatedGoodsCategory,
  cnCode: filterByCnCode,
  supplier: filterBySupplier,
  installation: filterByInstallation,
  emissionsReport: filterByEmissionsReport,
  country: filterByCountry,
  importer: filterByImporter,
  seller: filterBySeller,
  grrFilename: filterByGrrFilename,
  quarter: filterByQuarter
}

const doesGoodMatchFilter = (good: ImportedGoodExtended, filter: FilterValue): boolean =>
  filterFunctions[filter.type](good, filter.value)

/**
 * Applies all imported-goods filters in one place: toggles, list filters (including **OR** across
 * multiple `quarter` chips).
 */
function applyFilters<T extends ImportedGoodExtended>(
  goods: T[],
  filters: FilterValue[],
  toggleFilters: ToggleFilters,
  isRepresentative: boolean
): T[] {
  let result = goods

  if (toggleFilters.showOnlyDefaultValues) {
    result = result.filter(filterByDefaultValues)
  }
  if (toggleFilters.showOnlyIncomplete) {
    result = result.filter(good => filterByIncomplete(good, isRepresentative))
  }

  if (filters.length === 0) {
    return result
  }

  const quarterFilters = filters.filter(
    (filter): filter is FilterValue => filter.type === 'quarter'
  )
  const otherFilters = filters.filter((filter): filter is FilterValue => filter.type !== 'quarter')

  return result.filter(good => {
    const matchesOthers = otherFilters.every(filter => doesGoodMatchFilter(good, filter))
    const matchesQuarterGroup =
      quarterFilters.length === 0 ||
      quarterFilters.some(filter => doesGoodMatchFilter(good, filter))
    return matchesOthers && matchesQuarterGroup
  })
}

// Filter option builders
function buildAggregatedGoodsCategoryOptions(
  uniqueCategories: Set<AggregatedGoodsCategory>,
  getCategoryLabel: (category: AggregatedGoodsCategory) => string
) {
  return Array.from(uniqueCategories)
    .sort()
    .map(category => ({ value: category, label: getCategoryLabel(category) }))
}

function buildCnCodeOptions(uniqueCnCodes: Set<string>, getCnCodeLabel: (code: string) => string) {
  return Array.from(uniqueCnCodes)
    .sort()
    .map(cnCode => ({ value: cnCode, label: `${cnCode} - ${getCnCodeLabel(cnCode)}` }))
}

function buildSupplierOptions(
  uniqueSupplierIds: Set<string>,
  partnerships: Partnership[],
  noneLabel: string
) {
  const options = Array.from(uniqueSupplierIds)
    .map(supplierId => {
      const partnership = partnerships.find(partner => partner.id === supplierId)
      return partnership ? { value: supplierId, label: partnership.name } : null
    })
    .filter((option): option is { value: string; label: string } => option !== null)
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

function buildInstallationOptions(
  uniqueInstallationIds: Set<string>,
  installations: Installation[],
  noneLabel: string
) {
  const options = Array.from(uniqueInstallationIds)
    .map(installationId => {
      const installation = installations.find(inst => inst.id === installationId)
      return installation ? { value: installationId, label: installation.name } : null
    })
    .filter((option): option is { value: string; label: string } => option !== null)
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

function buildEmissionsReportOptions(
  uniqueEmissionsReportIds: Set<string>,
  emissionsReports: SupplierEmissionsReport[],
  noneLabel: string
) {
  const options = Array.from(uniqueEmissionsReportIds)
    .map(reportId => {
      const report = emissionsReports.find(emReport => emReport.id === reportId)
      if (!report) {
        return null
      }
      const reportLabel =
        report.goods.map(goodItem => goodItem.goodsDescription).join(', ') ||
        `Report ${reportId.slice(0, 8)}`
      return { value: reportId, label: reportLabel }
    })
    .filter((option): option is { value: string; label: string } => option !== null)
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

function buildCountryOptions(
  uniqueCountries: Set<string>,
  getCountryLabel: (code: string) => string
) {
  return Array.from(uniqueCountries)
    .sort()
    .map(countryCode => ({ value: countryCode, label: getCountryLabel(countryCode) }))
}

function buildSellerOptions(uniqueSellers: Set<string>, noneLabel: string) {
  const options = Array.from(uniqueSellers)
    .sort()
    .map(sellerName => ({ value: sellerName, label: sellerName }))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

function buildGrrFilenameOptions(uniqueGrrFilenames: Set<string>, noneLabel: string) {
  const options = Array.from(uniqueGrrFilenames)
    .sort((fileNameA, fileNameB) => fileNameA.localeCompare(fileNameB))
    .map(fileName => ({ value: fileName, label: fileName }))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

function buildQuarterOptions(reportingPeriodYear: number) {
  return QUARTERS.map(quarter => ({ value: quarter, label: `${quarter} ${reportingPeriodYear}` }))
}

function buildImporterOptions(
  uniqueImporterIds: Set<string>,
  importers: Importer[],
  noneLabel: string
) {
  const options = Array.from(uniqueImporterIds)
    .map(importerId => {
      const importer = importers.find(imp => imp.id === importerId)
      return importer ? { value: importerId, label: importer.name } : null
    })
    .filter((option): option is { value: string; label: string } => option !== null)
    .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label))

  options.unshift({ value: FILTER_NONE_VALUE, label: noneLabel })
  return options
}

export type NoneLabels = {
  noSupplier: string // Translation key, e.g., 'cbamReporting:filters.noSupplier'
  noInstallation: string // Translation key, e.g., 'cbamReporting:filters.noInstallation'
  noEmissionsReport: string // Translation key, e.g., 'cbamReporting:filters.noEmissionsReport'
  noImporter: string // Translation key, e.g., 'cbamReporting:filters.noImporter'
  noSeller: string // Translation key, e.g., 'cbamReporting:filters.noSeller'
  noGrrFilename: string // Translation key, e.g., 'cbamReporting:filters.noGrrFilename'
}

// Pure function: build filter options from goods data
function buildFilterOptions(
  goods: ImportedGoodExtended[],
  partnerships: Partnership[],
  installations: Installation[],
  emissionsReports: SupplierEmissionsReport[],
  importers: Importer[],
  isRepresentative: boolean,
  reportingPeriodYear: number,
  getCountryLabel: (code: string) => string,
  getCategoryLabel: (category: AggregatedGoodsCategory) => string,
  getCnCodeLabel: (code: string) => string,
  noneLabels: NoneLabels
): FilterOptions {
  const uniqueCategories = new Set<AggregatedGoodsCategory>()
  const uniqueCnCodes = new Set<string>()
  const uniqueSupplierIds = new Set<string>()
  const uniqueInstallationIds = new Set<string>()
  const uniqueEmissionsReportIds = new Set<string>()
  const uniqueCountries = new Set<string>()
  const uniqueImporterIds = new Set<string>()
  const uniqueSellers = new Set<string>()
  const uniqueGrrFilenames = new Set<string>()

  goods.forEach(good => {
    const productionMethod = productionMethodsByCode[good.productionMethodCode as ProductionMethods]
    if (productionMethod?.aggregatedGoodsCategory) {
      uniqueCategories.add(productionMethod.aggregatedGoodsCategory)
    }

    uniqueCnCodes.add(good.cnCode)
    uniqueCountries.add(good.country)

    if (good.partnershipId) {
      uniqueSupplierIds.add(good.partnershipId)
    }
    if (good.installationId) {
      uniqueInstallationIds.add(good.installationId)
    }
    if (good.supplierEmissionsReportId) {
      uniqueEmissionsReportIds.add(good.supplierEmissionsReportId)
    }
    if (good.importerId) {
      uniqueImporterIds.add(good.importerId)
    }
    if (good.sellerName) {
      uniqueSellers.add(good.sellerName)
    }
    if (good.goodsReceivedReportFileName) {
      uniqueGrrFilenames.add(good.goodsReceivedReportFileName)
    }
  })

  return {
    aggregatedGoodsCategories: buildAggregatedGoodsCategoryOptions(
      uniqueCategories,
      getCategoryLabel
    ),
    cnCodes: buildCnCodeOptions(uniqueCnCodes, getCnCodeLabel),
    suppliers: buildSupplierOptions(uniqueSupplierIds, partnerships, noneLabels.noSupplier),
    installations: buildInstallationOptions(
      uniqueInstallationIds,
      installations,
      noneLabels.noInstallation
    ),
    emissionsReports: buildEmissionsReportOptions(
      uniqueEmissionsReportIds,
      emissionsReports,
      noneLabels.noEmissionsReport
    ),
    countries: buildCountryOptions(uniqueCountries, getCountryLabel),
    importers: isRepresentative
      ? buildImporterOptions(uniqueImporterIds, importers, noneLabels.noImporter)
      : [],
    sellers: buildSellerOptions(uniqueSellers, noneLabels.noSeller),
    grrFilenames: buildGrrFilenameOptions(uniqueGrrFilenames, noneLabels.noGrrFilename),
    quarters: buildQuarterOptions(reportingPeriodYear)
  }
}

const defaultToggleFilters: ToggleFilters = {
  showOnlyDefaultValues: false,
  showOnlyIncomplete: false
}

function filterEntryId(filter: FilterValue): string {
  return `${filter.type}:${filter.value}`
}

function appendActiveFilter(prev: FilterValue[], filter: FilterValue): FilterValue[] | null {
  if (SINGLE_SELECT_FILTER_TYPES.has(filter.type)) {
    const existing = prev.find(f => f.type === filter.type)
    if (existing && filterEntryId(existing) === filterEntryId(filter)) {
      return null
    }
    return [...prev.filter(f => f.type !== filter.type), filter]
  }
  const isDuplicate = prev.some(existing => filterEntryId(existing) === filterEntryId(filter))
  return isDuplicate ? null : [...prev, filter]
}

export function useImportedGoodsFilters(
  goods: ImportedGoodExtended[],
  partnerships: Partnership[],
  installations: Installation[],
  emissionsReports: SupplierEmissionsReport[],
  importers: Importer[],
  isRepresentative: boolean,
  reportingPeriodYear: number,
  getCountryLabel = defaultCountryLabel,
  getAggregatedGoodsCategoryLabel = defaultCategoryLabel,
  getCnCodeLabel = defaultCnCodeLabel,
  noneLabels: NoneLabels
) {
  const [activeFilters, setActiveFilters] = useState<FilterValue[]>([])
  const [toggleFilters, setToggleFilters] = useState<ToggleFilters>(defaultToggleFilters)
  const effectiveActiveFilters = useMemo(
    () =>
      isRepresentative ? activeFilters : activeFilters.filter(filter => filter.type !== 'importer'),
    [activeFilters, isRepresentative]
  )

  useEffect(() => {
    if (isRepresentative) {
      return
    }
    setActiveFilters(prev => {
      const sanitized = prev.filter(filter => filter.type !== 'importer')
      return sanitized.length === prev.length ? prev : sanitized
    })
  }, [isRepresentative])

  const filterOptions = useMemo<FilterOptions>(
    () =>
      buildFilterOptions(
        goods,
        partnerships,
        installations,
        emissionsReports,
        importers,
        isRepresentative,
        reportingPeriodYear,
        getCountryLabel,
        getAggregatedGoodsCategoryLabel,
        getCnCodeLabel,
        noneLabels
      ),
    [
      goods,
      partnerships,
      installations,
      emissionsReports,
      importers,
      isRepresentative,
      reportingPeriodYear,
      getCountryLabel,
      getAggregatedGoodsCategoryLabel,
      getCnCodeLabel,
      noneLabels
    ]
  )

  const filteredGoods = useMemo(
    () => applyFilters(goods, effectiveActiveFilters, toggleFilters, isRepresentative),
    [goods, effectiveActiveFilters, toggleFilters, isRepresentative]
  )

  const addFilter = useCallback(
    (filter: FilterValue) => {
      if (!isRepresentative && filter.type === 'importer') {
        return
      }
      let applied = false
      setActiveFilters(prev => {
        const next = appendActiveFilter(prev, filter)
        if (next === null) {
          return prev
        }
        applied = true
        return next
      })
      if (applied && filter.type === 'grrFilename' && !isNoneFilter(filter.value)) {
        trackCbamReportingSignal('reporting:filter:specific-grr:selected')
      }
    },
    [isRepresentative]
  )

  const removeFilter = useCallback((filter: FilterValue) => {
    setActiveFilters(prev =>
      prev.filter(existing => filterEntryId(existing) !== filterEntryId(filter))
    )
  }, [])

  const clearAllFilters = useCallback(() => {
    setActiveFilters([])
    setToggleFilters(defaultToggleFilters)
  }, [])

  const setShowOnlyDefaultValues = useCallback((value: boolean) => {
    setToggleFilters(prev => ({ ...prev, showOnlyDefaultValues: value }))
  }, [])

  const setShowOnlyIncomplete = useCallback((value: boolean) => {
    setToggleFilters(prev => ({ ...prev, showOnlyIncomplete: value }))
  }, [])

  return {
    activeFilters,
    filterOptions,
    filteredGoods,
    addFilter,
    removeFilter,
    clearAllFilters,
    hasActiveFilters:
      effectiveActiveFilters.length > 0 ||
      toggleFilters.showOnlyDefaultValues ||
      toggleFilters.showOnlyIncomplete,
    // Toggle filters
    toggleFilters,
    setShowOnlyDefaultValues,
    setShowOnlyIncomplete
  }
}

export type ImportedGoodsFiltersResult = ReturnType<typeof useImportedGoodsFilters>
